import { useEffect, useEffectEvent, useState, type CSSProperties } from 'react';
import type { ItemSpec, MatchView, Side } from '../../../engine';
import { t, type MessageKey } from '../../../i18n';
import { itemName } from '../../../app/describe';
import { sounds } from '../../../app/sound';
import { BrokerHand, Candle } from './Broker';
import { EYE } from './pixelBroker';
import { SpritePaths } from './Sprite';
import { mantelObject } from './Item';
import { Mantel } from './Mantel';
import { NightObject } from './Objects';

/**
 * The Long Night at the table (docs/LONG_NIGHT_ART_DIRECTION.md §11.6, §11.13): each side's kit
 * in its strip (yours with the count's start marked, theirs smaller, the Broker's under glass),
 * the Ante written in his ink beside their score, and the intermission between the rounds of a
 * High Table.
 */

/** A side's Mantel and Curio pockets, in its strip. Emblems only; a tap reads one. */
export function StripKit({
  v,
  side,
  mine,
  glass = false,
  curioReady,
  onItem,
  onCurio,
}: {
  v: MatchView;
  side: Side;
  /** This device's player: the full-size niches, the count's start, pockets that can be used. */
  mine: boolean;
  /** The Broker's own kit, kept under glass. */
  glass?: boolean;
  curioReady?: (item: ItemSpec) => boolean;
  onItem: (item: ItemSpec, kind: 'superstition' | 'curio') => void;
  onCurio?: (item: ItemSpec) => void;
}) {
  const l = v.ledger;
  const kit = v.config.ledger?.kits[side];
  if (!l || !kit) return null;
  const items = l.mantel[side];
  const curios = l.curios[side];
  // Theirs shows only what they have: the strip has no room for their empty niches.
  if (!mine && items.length === 0 && curios.length === 0) return null;
  return (
    <span
      className={`strip-kit ${mine ? 'is-mine' : 'is-theirs'} ${glass ? 'is-glass' : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      {(mine || items.length > 0) && (
        <Mantel
          items={items}
          slots={mine ? (kit.slots ?? 5) : items.length}
          size={mine ? 'table' : 'glass'}
          countStart={mine}
          onOpen={(item) => onItem(item, 'superstition')}
        />
      )}
      {curios.length > 0 && (
        <span className="strip-pockets">
          {curios.map((c) => {
            const ready = mine && !!curioReady?.(c);
            return (
              <button
                key={c.uid}
                type="button"
                className={`strip-pocket ${ready ? 'is-ready' : ''}`}
                aria-label={itemName(c.id)}
                title={itemName(c.id)}
                onClick={() => (ready && onCurio ? onCurio(c) : onItem(c, 'curio'))}
              >
                <NightObject spec={{ kind: 'curio', emblem: c.id }} size={mine ? 20 : 16} />
              </button>
            );
          })}
        </span>
      )}
    </span>
  );
}

/**
 * The Ante (§12.12): written by his hand, in his ink, beside their score at the start of every
 * round. Never a chip, never brass. Keyed by the round, so each round writes it again.
 */
export function Ante({ n, still }: { n: number; still: boolean }) {
  const [hand, setHand] = useState(!still);
  useEffect(() => {
    if (still) return;
    sounds.play('nib');
    const id = setTimeout(() => setHand(false), 1500);
    return () => clearTimeout(id);
  }, [still]);
  return (
    <span className={`strip-ante ${still ? 'is-still' : ''}`} title={t('night.anteValue', { n })}>
      {hand && <BrokerHand pose="reach" at={0.6} still={still} />}
      <span className="strip-ante-n">+{n}</span>
    </span>
  );
}

const ROUND_WORDS = ['one', 'two', 'three', 'four', 'five'];

/** How long the interlude lasts (ms), with motion and without. */
const INTERLUDE_MS = 3000;
const INTERLUDE_STILL_MS = 1800;

/** Where the eyes open in the dark, around the candle and out of its light. */
function eyeSpots(cx: number, cy: number): { x: number; y: number; w: number; d: number }[] {
  const w = window.innerWidth;
  const h = window.innerHeight;
  const out: { x: number; y: number; w: number; d: number }[] = [];
  for (let i = 0; out.length < 9 && i < 200; i++) {
    const size = 24 + Math.random() * 30;
    const x = size * 1.3 + Math.random() * (w - size * 2.6);
    const y = 40 + Math.random() * (h - 80);
    // The candle's light, and the round's name under it (styles.css, .im-dark and .im-round).
    if (Math.hypot((x - cx) / 160, (y - cy + 24) / 150) < 1) continue;
    if (Math.abs(x - cx) < 150 && y > cy && y < cy + 150) continue;
    if (out.some((e) => Math.hypot(e.x - x, e.y - y) < 70)) continue;
    // One after another, quickly, once the room is dark, until it all lifts.
    out.push({ x, y, w: size, d: 1450 + out.length * 110 });
  }
  return out;
}

/**
 * Between the rounds of a High Table (§11.13): a candle is dropped onto the middle of the board
 * and catches. Its light holds a small soft circle while the rest of the room goes slowly pitch
 * dark; then eyes open here and there in the dark, watch, and are gone, one after another. In
 * under three seconds it all lifts and the next round begins; a tap goes on at once. With
 * reduced motion the candle simply stands lit in the dark, and no eyes come.
 */
export function Intermission({
  next,
  result,
  still,
  onDone,
}: {
  next: number;
  /** How the round that ended went, in one line. */
  result: string;
  still: boolean;
  onDone: () => void;
}) {
  const done = useEffectEvent(onDone);
  // The middle of the board, where the candle lands (the screen's middle without one).
  const [at] = useState(() => {
    const b = document.querySelector('.board')?.getBoundingClientRect();
    return b
      ? { x: b.left + b.width / 2, y: b.top + b.height / 2 }
      : { x: window.innerWidth / 2, y: window.innerHeight / 2 };
  });
  const [eyes] = useState(() => (still ? [] : eyeSpots(at.x, at.y)));
  useEffect(() => {
    const timers = [setTimeout(done, still ? INTERLUDE_STILL_MS : INTERLUDE_MS)];
    if (!still) {
      timers.push(setTimeout(() => sounds.play('thud'), 330));
      timers.push(setTimeout(() => sounds.play('match'), 420));
      timers.push(setTimeout(() => sounds.play('breath'), 1400));
    }
    return () => timers.forEach(clearTimeout);
  }, [still]);
  return (
    <div
      className={`intermission ${still ? 'is-still' : ''}`}
      style={{ '--cx': `${at.x}px`, '--cy': `${at.y}px` } as CSSProperties}
      role="status"
      onClick={onDone}
    >
      <span className="im-dark" aria-hidden="true" />
      <span className="im-glow" aria-hidden="true" />
      <span className="im-candle" aria-hidden="true">
        <Candle night={1} size={84} />
      </span>
      {eyes.map((e, i) => (
        <span
          key={i}
          className="im-eyes"
          style={
            {
              left: e.x,
              top: e.y,
              '--w': `${e.w}px`,
              animationDelay: `${e.d}ms`,
            } as CSSProperties
          }
          aria-hidden="true"
        >
          <svg viewBox={`0 0 ${EYE.w} ${EYE.h}`} shapeRendering="crispEdges">
            <SpritePaths sprite={EYE} />
          </svg>
          <svg viewBox={`0 0 ${EYE.w} ${EYE.h}`} shapeRendering="crispEdges" className="is-mirror">
            <SpritePaths sprite={EYE} />
          </svg>
        </span>
      ))}
      <p className="im-round">
        {t(`night.round.${ROUND_WORDS[next - 1] ?? 'two'}` as MessageKey)}
        <span>{result}</span>
      </p>
    </div>
  );
}

/**
 * A Superstition taken or lost at the table (§11.17). Taken: his hand comes down and lifts it
 * away, to be kept under glass on his side. Foreclosed: a line of red ink strikes through it, and
 * it fades away. A tap goes on.
 */
export function ItemMoment({
  reason,
  id,
  still,
  onDone,
}: {
  reason: 'stolen' | 'foreclosed';
  id: string;
  still: boolean;
  onDone: () => void;
}) {
  const done = useEffectEvent(onDone);
  useEffect(() => {
    sounds.play(reason === 'stolen' ? 'glass' : 'scratch');
    const timer = setTimeout(done, still ? 1600 : 2200);
    return () => clearTimeout(timer);
  }, [still, reason]);
  const object = mantelObject({ uid: id, id });
  return (
    <div
      className={`item-moment is-${reason} ${still ? 'is-still' : ''}`}
      role="status"
      onClick={onDone}
    >
      {reason === 'stolen' ? (
        <BrokerHand
          pose="reach"
          at={0.5}
          to={0.45}
          still={still}
          holding={<NightObject spec={object} size={64} />}
        />
      ) : (
        <span className="im-item">
          <NightObject spec={object} size={76} />
          <span className="im-strike" aria-hidden="true" />
        </span>
      )}
      <p className="im-text">
        {t(reason === 'stolen' ? 'night.moment.stolen' : 'night.moment.foreclosed', {
          name: itemName(id),
        })}
      </p>
    </div>
  );
}
